import { formatClientName } from './orderDisplay'

/**
 * Filter-field registry. Each field owns its label, its option list, and its
 * match predicate — so the Filters modal ("+ Add filter"), the toolbar
 * active-count, and selectFilteredOrders all iterate ONE source of truth.
 * Adding a new filter later = add one entry here + one key on DashboardFilters.
 */
export type FilterKey =
  | 'lifecycles'
  | 'vehicles'
  | 'vehicleTypes'
  | 'drivers'
  | 'clients'
  | 'serviceTypes'

export interface FilterCtx {
  orders: any[]
  /** Fleet vehicles (selectTrucks output): { name, vehicleType }. */
  trucks: { name: string; vehicleType: string }[]
  /** Region drivers (selectRegionDriversDashboard output): { label: "Name***user" }. */
  drivers: { label: string }[]
  /** Ordered lifecycle names (selectSubstatusMaps().lifecycles). */
  lifecycles: string[]
  /** OrderStatus → lifecycle name. */
  substatusMap: Record<string, string>
}

export interface FilterFieldDef {
  key: FilterKey
  label: string
  /** Option values shown in this field's multiselect. */
  getOptions: (ctx: FilterCtx) => string[]
  /** True if the order passes this field given the (non-empty) selection. */
  matches: (order: any, selected: string[], ctx: FilterCtx) => boolean
}

function uniq<T>(arr: T[]): T[] { return Array.from(new Set(arr)) }

/** "Unassigned"-aware membership for assignable fields (vehicle/driver). */
function matchesAssignable(selected: string[], value: string): boolean {
  if (!value) return selected.includes('Unassigned')
  return selected.includes(value)
}

export const FILTER_FIELDS: FilterFieldDef[] = [
  {
    key: 'lifecycles',
    label: 'Lifecycle',
    getOptions: (ctx) => ctx.lifecycles.slice(),
    matches: (o, selected, ctx) => selected.includes(ctx.substatusMap[o.OrderStatus] || ''),
  },
  {
    key: 'vehicles',
    label: 'Vehicle',
    getOptions: (ctx) => {
      const fromOrders = ctx.orders.map((o) => o.Vehicle).filter(Boolean) as string[]
      const fromFleet = ctx.trucks.map((t) => t.name).filter(Boolean)
      return ['Unassigned', ...uniq([...fromFleet, ...fromOrders])]
    },
    matches: (o, selected) => matchesAssignable(selected, o.Vehicle || ''),
  },
  {
    key: 'vehicleTypes',
    label: 'Vehicle Type',
    getOptions: (ctx) => {
      const fromOrders = ctx.orders.map((o) => o.vehicleType).filter(Boolean) as string[]
      const fromFleet = ctx.trucks.map((t) => t.vehicleType).filter(Boolean)
      return uniq([...fromFleet, ...fromOrders])
    },
    matches: (o, selected) => selected.includes(o.vehicleType || ''),
  },
  {
    key: 'drivers',
    label: 'Driver',
    getOptions: (ctx) => {
      const fromOrders = ctx.orders.map((o) => o.Driver).filter(Boolean) as string[]
      const fromAvailable = ctx.drivers.map((d) => (d.label || '').split('***')[0]).filter(Boolean)
      return ['Unassigned', ...uniq([...fromAvailable, ...fromOrders])]
    },
    matches: (o, selected) => matchesAssignable(selected, o.Driver || ''),
  },
  {
    key: 'clients',
    label: 'Client',
    getOptions: (ctx) => uniq(ctx.orders.map((o) => formatClientName(o.client)).filter(Boolean)),
    matches: (o, selected) => selected.includes(formatClientName(o.client)),
  },
  {
    key: 'serviceTypes',
    label: 'Service Type',
    getOptions: (ctx) => uniq(ctx.orders.map((o) => o.serviceType).filter(Boolean) as string[]),
    matches: (o, selected) => selected.includes(o.serviceType || ''),
  },
]

export const FILTER_FIELD_BY_KEY: Record<FilterKey, FilterFieldDef> =
  FILTER_FIELDS.reduce((acc, f) => { acc[f.key] = f; return acc }, {} as Record<FilterKey, FilterFieldDef>)
